import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import equal from 'fast-deep-equal';
import { cloneDeep } from 'lodash-es';
import isEmpty from 'lodash-es/isEmpty';
import { BetDateType } from '@/constants';
import { globals } from '@/globals';
import { formatDateFilterForApi, pageReload } from '@/helpers';
import { setRemoteDictionaries } from '@/helpers/dictionary';
import { webSocketClientGetUserData, webSocketClientSendObj, webSocketClientSetUserData } from '@/services/webSocketClient';
import { useAppStore } from '@/stores/app';
import { useUserStore } from '@/stores/user';
import { IApiListParams, IBaseApiListResponse, IListParams } from '@/types';

const activeListControllers = new Map<string, AbortController>();

function getListRequestKey<T extends IBaseApiListResponse>(
    apiCallFn: (params: IApiListParams, config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>,
    params: IListParams
): string {
    return params.requestKey ?? apiCallFn.name ?? 'base-list-service';
}

export function isRequestCanceled(error: unknown): boolean {
    return axios.isCancel(error) || (typeof error === 'object' && error !== null && 'code' in error && error.code === 'ERR_CANCELED');
}

function getDateFilterTimezone(rawFilters: Record<string, any>, userTimezone: string): string {
    const betDateType = rawFilters.betDateType;
    const betDateTypeValue = betDateType && typeof betDateType === 'object' && 'value' ? betDateType.value : '';

    switch (betDateTypeValue) {
        case BetDateType.PlacementLondon:
        case BetDateType.SettlementLondon:
            return 'Europe/London';
        case BetDateType.PlacementGMT:
        case BetDateType.SettlementGMT:
            return 'GMT';
        default:
            return userTimezone;
    }
}

function transformFilters(rawFilters: Record<string, any>, userTimezone: string): Record<string, any> {
    const dateFilterTimezone = getDateFilterTimezone(rawFilters, userTimezone);

    return Object.fromEntries(
        Object.entries(rawFilters)
            .map(([key, value]) => {
                let val = value && typeof value === 'object' && 'value' in value ? value.value : value;
                if (typeof val === 'string') {
                    val = val.trimStart();
                }
                if (val && typeof val === 'object' && !Array.isArray(val) && !(val instanceof Date) && 'id' in val) {
                    val = val.id;
                }
                return [key, val];
            })
            .filter(([, v]) => v !== '' && v !== null && (!Array.isArray(v) || v.length !== 0))
            .flatMap(([key, value]) => {
                if (key.toLowerCase().includes('date') && Array.isArray(value) && value.length === 2) {
                    return [
                        [`${key}From`, formatDateFilterForApi(value[0], dateFilterTimezone)],
                        [`${key}To`, formatDateFilterForApi(value[1], dateFilterTimezone)],
                    ];
                }
                if (key.toLowerCase().includes('date') && value instanceof Date) {
                    return [[key, formatDateFilterForApi(value, dateFilterTimezone)]];
                }
                return [[key, value]];
            })
    );
}

export async function baseListService<T extends IBaseApiListResponse>(
    apiCallFn: (params: IApiListParams, config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>,
    params: IListParams
): Promise<AxiosResponse<T>> {
    const appStore = useAppStore();
    const userStore = useUserStore();
    const requestKey = getListRequestKey(apiCallFn, params);
    const previousController = activeListControllers.get(requestKey);
    const controller = new AbortController();

    console.log('Load data from server');

    previousController?.abort();
    activeListControllers.set(requestKey, controller);

    const start = (params.options.page - 1) * params.options.itemsPerPage;
    const sortDirectionCase = params.sortDirectionCase ?? 'lower';
    const sort = params.options.sortBy.map(({ key, order }) => ({
        property: key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase(), // convert to kebab-case
        direction: sortDirectionCase === 'upper' ? order.toUpperCase() : order,
    }));

    //const filters = Object.fromEntries(Object.entries(params.filters).filter(([, v]) => v !== '' && v !== null && (!Array.isArray(v) || v.length !== 0)));

    let filters: Record<string, any> = {};

    if (params.filters) {
        filters = transformFilters(params.filters, userStore.timeZone);
    }

    let requiredDictionaries = cloneDeep(params.requiredDictionaries).filter((item) => item !== 'TimeZone' && item !== 'SiteAll');
    requiredDictionaries.push('TimeZone');
    if (!userStore.isAgentPlatform()) {
        requiredDictionaries.push('SiteAll');
    }

    let paramsData: any = {
        page: params.options.page,
        filter: filters,
        start,
        limit: params.options.itemsPerPage,
        sort: JSON.stringify(sort),
        requiredDictionaries: requiredDictionaries,
        requiredData: params.requiredData,
    };

    if (!appStore.isInitialized) {
        paramsData.init = true;
    }

    if (params.extraParams) {
        Object.assign(paramsData, params.extraParams);
    }

    const pageUrl = window.location.toString();

    try {
        const response = await apiCallFn(paramsData, { signal: controller.signal });
        if (response.data.success !== false) {
            const data = response.data;

            if (data.config?.scriptVersion) {
                const oldVersion = localStorage.getItem('scriptVersion');
                if (oldVersion !== data.config.scriptVersion.toString()) {
                    localStorage.setItem('scriptVersion', data.config.scriptVersion.toString());
                    if (oldVersion) pageReload();
                }
            }

            if (data.config && !Array.isArray(data.config)) {
                if (data.config.scriptVersion) appStore.config.scriptVersion = data.config.scriptVersion;
            }

            if (data.dictionary) setRemoteDictionaries(data.dictionary);

            appStore.isInitialized = true;

            if (params.updateUserData !== false && pageUrl === window.location.toString()) {
                const payload = {
                    pageUrl,
                    pageCode: globals.router.currentRoute.value.name,
                    filter: !isEmpty(filters) ? filters : undefined,
                };

                if (!equal(webSocketClientGetUserData(), payload)) {
                    webSocketClientSendObj({
                        event: 'updateUserData',
                        data: payload,
                    });
                    webSocketClientSetUserData(payload);
                }
            }
        }

        return response;
    } catch (e) {
        throw e;
    } finally {
        if (activeListControllers.get(requestKey) === controller) {
            activeListControllers.delete(requestKey);
        }
    }
}

export async function baseListExportService(
    apiCallFn: (params: IApiListParams) => Promise<AxiosResponse<Blob>>,
    params: IListParams
): Promise<AxiosResponse<Blob>> {
    const userStore = useUserStore();

    console.log('Load export data from server');

    const start = (params.options.page - 1) * params.options.itemsPerPage;
    const sort = [{ property: 'created_at', direction: 'desc' }];

    //const filters = Object.fromEntries(Object.entries(params.filters).filter(([, v]) => v !== '' && v !== null && (!Array.isArray(v) || v.length !== 0)));

    let filters: Record<string, any> = {};

    if (params.filters) {
        filters = transformFilters(params.filters, userStore.timeZone);
    }

    let paramsData: any = {
        page: params.options.page,
        filter: filters,
        start,
        limit: params.options.itemsPerPage,
        sort: JSON.stringify(sort),
        toXls: 1,
        columns: params.columns,
    };

    if (params.extraParams) {
        Object.assign(paramsData, params.extraParams);
    }

    try {
        return await apiCallFn(paramsData);
    } catch (e) {
        throw e;
    }
}
